import React, {FC} from 'react';
import {connect} from 'react-redux';
import Select from '../Select/Select';
import User from '@/entities/user';
import Group from '@/entities/group';
import {sortDeletedUsers} from "@/Contacts/contactSorter";
import {formatUserName} from "@/Contacts/ContactFormat";
import ErrorBoundaryO from "@/ErrorBoundary/ErrorBoundaryO";
import IconButton from "../IconButton/IconButton";
import Icons from "../IconButton/Icons";
import type {RootState} from "@/@types/store";

type UserSelectProps = Pick<RootState, 'users' | 'groups'> &
    {
        allowNone?: boolean;
        autoAdvance?: boolean;
        disabled?: boolean;
        disablePortaling?: boolean;
        defaultValue?: User | Group | (User | Group)[]; // Single or array for isMulti
        groupsLast?: boolean, // Should we show the groups last in the select?
        includeDeleted?: boolean; // Include deleted users?
        includeDisabled?: boolean; // Include disabled users?
        includeGroups?: boolean; // Include groups as well as users?
        isMulti?: boolean;
        name: string; // unique name for the select component
        onClick: (value: any) => void; // Adjust the type of `value` based on the actual data structure expected
        overrideUsers?: User[];
        styles?: React.CSSProperties;
    };

const UserSelect: FC<UserSelectProps> = props => {
    // Map to the component cache in redux
    const users = props.overrideUsers || props.users;
    const groups = props.groups;

    const getColor = (user: User) => {
        if (user.status === 'D') return 'maroon';
        else if (user.enabled === false) return 'gray';
        else return 'black';
    }

    function getOptions() {
        const _groups = props.includeGroups
                        ? groups
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter(g => props.includeDeleted ? true : g.status !== 'D')
                            .map(group => {
                                return {
                                    label: <IconButton type={Icons.GROUP}
                                                       label={group.name}
                                                       labelBold={true}
                                                       color='navy'
                                                       textColor='navy'
                                    />,
                                    value: group.name,
                                    data: group
                                }
                            })
                        : [];

        const _users = users
            .sort(sortDeletedUsers)
            .filter(user => props.includeDisabled ? true : user.enabled)
            .filter(user => props.includeDeleted ? true : user.status !== 'D')
            .map(user => {
                const userName = formatUserName(user)
                const label = user.status === 'D'
                              ? <span className='u-deleted'>{userName}</span>
                              : user.enabled === false
                                ? <span className='task-item--disabled'>{userName}</span>
                                : userName;

                return {
                    label: <IconButton type={Icons.USER}
                                       label={label}
                                       color={getColor(user)}
                                       textColor={getColor(user)}
                    />,
                    value: formatUserName(user),
                    data: user
                };
            });

        if (props.groupsLast) return [..._users, ..._groups];
        else return [..._groups, ..._users];

        // TODO: Started trying to group, doesn't work yet.
        //
        // const userGrouping = {
        //     label: <IconButton type={Icons.GROUP} label='Groups' color='navy' textColor='navy'/>,
        //     options: _groups
        // };
        //
        // const groupGrouping = {
        //     label: 'Users',
        //     options: _users
        // }
        //
        //
        // if (props.groupsLast) return [userGrouping, groupGrouping];
        // else return [groupGrouping, userGrouping];
    }

    // Convert a single User or Group to a SelectOption
    const toSelectOption = (item: User | Group) => {
        const itemName = item instanceof User ? formatUserName(item) : item.name;
        const itemIcon = item instanceof User
            ? <IconButton type={Icons.USER} label={itemName} color='black' textColor='black'/>
            : <IconButton type={Icons.GROUP} label={itemName} labelBold={true}/>;
        return {label: itemIcon, value: itemName, data: item};
    };

    // Build defaultValue: single option or array of options for isMulti
    const defaultValue = props.defaultValue
        ? Array.isArray(props.defaultValue)
            ? props.defaultValue.map(toSelectOption)
            : toSelectOption(props.defaultValue)
        : null

    return (
        <ErrorBoundaryO uniqueKey='user-select' shouldUseErrorIcon={true}>
            <Select name={`user-select-${props.name}`}
                    options={getOptions()}
                    allowCreate={false}
                    autoAdvance={props.autoAdvance}
                    onChange={props.onClick}
                    defaultValue={defaultValue}
                    allowNone={props.allowNone}
                    disabled={props.disabled}
                    isMulti={props.isMulti}
                    disablePortaling={props.disablePortaling}
                    usesExtraFields={true}
                    styles={props.styles}
            />
        </ErrorBoundaryO>
    );
};

const mapStateToProps = (state: RootState) => ({
    users: state.users,
    groups: state.groups
});

export default connect(mapStateToProps)(UserSelect);